import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessingStageTimeline = ({ stages, currentStage }) => {
  const getStageIcon = (stage) => {
    switch (stage?.type) {
      case 'normalization':
        return 'Filter';
      case 'exact-ean':
        return 'Hash';
      case 'brand-mpn':
        return 'Tag';
      case 'fuzzy-matching':
        return 'Brain';
      default:
        return 'Circle';
    }
  };

  const getStageStatus = (stage, index) => {
    if (stage?.status === 'completed') return 'completed';
    if (stage?.status === 'processing') return 'processing';
    if (stage?.status === 'error') return 'error';
    if (index < currentStage) return 'completed';
    if (index === currentStage) return 'current';
    return 'pending';
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'completed':
        return {
          container: 'bg-success border-success',
          icon: 'text-success-foreground',
          connector: 'bg-success'
        };
      case 'processing':
        return {
          container: 'bg-warning border-warning animate-pulse',
          icon: 'text-warning-foreground',
          connector: 'bg-warning'
        };
      case 'error':
        return {
          container: 'bg-error border-error',
          icon: 'text-error-foreground',
          connector: 'bg-error'
        };
      case 'current':
        return {
          container: 'bg-primary border-primary ring-2 ring-primary ring-opacity-20',
          icon: 'text-primary-foreground',
          connector: 'bg-border'
        };
      default:
        return {
          container: 'bg-muted border-border',
          icon: 'text-muted-foreground',
          connector: 'bg-border'
        };
    }
  };

  return (
    <div className="space-y-6">
      {stages?.map((stage, index) => {
        const status = getStageStatus(stage, index);
        const styles = getStatusStyles(status);

        return (
          <div key={stage?.id} className="relative">
            {/* Timeline Connector */}
            {index < stages?.length - 1 && (
              <div className={`
                absolute left-6 top-12 w-0.5 h-16 transition-colors duration-300
                ${styles?.connector}
              `} />
            )}
            <div className="flex items-start space-x-4">
              {/* Stage Icon */}
              <div className={`
                flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300
                ${styles?.container}
              `}>
                {status === 'processing' ? (
                  <Icon name="Loader2" size={20} className={`${styles?.icon} animate-spin`} />
                ) : status === 'completed' ? (
                  <Icon name="Check" size={20} className={styles?.icon} />
                ) : status === 'error' ? (
                  <Icon name="X" size={20} className={styles?.icon} />
                ) : (
                  <Icon name={getStageIcon(stage)} size={20} className={styles?.icon} />
                )}
              </div>

              {/* Stage Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-foreground">
                    {stage?.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {status === 'processing' && (
                      <span className="text-sm text-warning font-medium">
                        {Math.round(stage?.progress)}%
                      </span>
                    )}
                    {stage?.itemsProcessed > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {stage?.itemsProcessed?.toLocaleString()} / {stage?.totalItems?.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-3">
                  {stage?.description}
                </p>

                {/* Progress Bar */}
                {status === 'processing' && (
                  <div className="mb-3">
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-warning transition-all duration-500 ease-out"
                        style={{ width: `${Math.min(stage?.progress, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Stage Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <div className={`font-medium ${
                      status === 'completed' ? 'text-success' :
                      status === 'processing' ? 'text-warning' :
                      status === 'error'? 'text-error' : 'text-muted-foreground'
                    }`}>
                      {status === 'completed' ? 'Complete' :
                       status === 'processing' ? 'Processing' :
                       status === 'error' ? 'Error' :
                       status === 'current' ? 'Ready' : 'Pending'}
                    </div>
                  </div>

                  {stage?.matchesFound !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Matches:</span>
                      <div className="font-medium text-foreground">
                        {stage?.matchesFound?.toLocaleString()}
                      </div>
                    </div>
                  )}

                  {stage?.processingSpeed && (
                    <div>
                      <span className="text-muted-foreground">Speed:</span>
                      <div className="font-medium text-foreground">
                        {stage?.processingSpeed}/sec
                      </div>
                    </div>
                  )}

                  {stage?.estimatedTime && status === 'processing' && (
                    <div>
                      <span className="text-muted-foreground">ETA:</span>
                      <div className="font-medium text-foreground">
                        {stage?.estimatedTime}
                      </div>
                    </div>
                  )}
                </div>

                {/* Current Operation */}
                {status === 'processing' && stage?.currentOperation && (
                  <div className="mt-3 p-2 bg-muted rounded-md">
                    <div className="flex items-center space-x-2">
                      <Icon name="Activity" size={14} className="text-warning" />
                      <span className="text-sm text-foreground">
                        {stage?.currentOperation}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error Details */}
                {status === 'error' && stage?.error && (
                  <div className="mt-3 p-3 bg-error bg-opacity-10 border border-error border-opacity-20 rounded-md">
                    <div className="flex items-start space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-error mb-1">
                          Processing Error
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {stage?.error}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProcessingStageTimeline;